import React, { useEffect } from 'react';
import { Tabs } from 'antd';
import styled from 'styled-components/macro';
import { useTranslation } from '../../../../../../i18n/useTranslation';

import { EntityTab } from '../../../types';
import { useBaseEntity, useEntityData, useRouteToTab } from '../../../EntityContext';

type Props = {
    tabs: EntityTab[];
    selectedTab?: EntityTab;
};

const Tab = styled(Tabs.TabPane)`
    font-size: 14px;
    line-height: 22px;
`;

export const EntityTabs = <T,>({ tabs, selectedTab }: Props) => {
    const { entityData, loading } = useEntityData();
    const routeToTab = useRouteToTab();
    const baseEntity = useBaseEntity<T>();
    const { t } = useTranslation();

    const enabledTabs = tabs.filter((tab) => tab.display?.enabled(entityData, baseEntity));

    useEffect(() => {
        if (!loading && !selectedTab && enabledTabs[0]) {
            routeToTab({ tabName: enabledTabs[0].name, method: 'replace' });
        }
    }, [loading, enabledTabs, selectedTab, routeToTab]);

    // Don't render tabs until we have data or at least know what tabs are available
    if (loading || enabledTabs.length === 0) {
        return null;
    }

    return (
        <Tabs
            activeKey={selectedTab?.name || ''}
            size="large"
            onTabClick={(tab: string) => routeToTab({ tabName: tab })}
        >
            {tabs.map((tab, index) => {
                const tabId = `tab-${index}`; // Assign a unique ID for each tab content
                const translatedTabName = t(tab.name);
                if (!tab.display?.enabled(entityData, baseEntity)) {
                    return <Tab tab={translatedTabName} key={tab.name} disabled aria-controls={tabId} />;
                }
                return <Tab tab={translatedTabName} key={tab.name} aria-controls={tabId} />;
            })}
        </Tabs>
    );
};
